import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiFetch } from '../api/client';
import logger from '../utils/logger';

interface StaffPermissions {
    staffCanViewAnalytics: boolean;
    staffCanViewCashflow: boolean;
    staffCanManageMenu: boolean;
    staffCanViewDocuments: boolean;
    staffCanManageEvents: boolean;
    staffCanViewOrders: boolean;
    staffCanManageLocations: boolean;
    staffCanViewBranding: boolean;
    staffCanDeleteOrders: boolean;
    staffCanManagePrintCenter: boolean;
    staffCanManageUsers: boolean;
    staffCanViewPosStatistics: boolean;
    staffCanManageCashSessions: boolean;
}

interface PermissionsContextType {
    permissions: StaffPermissions;
    loading: boolean;
    isStaff: boolean;
    isTenantAdmin: boolean;
    canAccess: (permission: keyof StaffPermissions) => boolean;
    refreshPermissions: () => Promise<void>;
}

const defaultPermissions: StaffPermissions = {
    staffCanViewAnalytics: false,
    staffCanViewCashflow: false,
    staffCanManageMenu: false,
    staffCanViewDocuments: false,
    staffCanManageEvents: false,
    staffCanViewOrders: true,
    staffCanManageLocations: false,
    staffCanViewBranding: false,
    staffCanDeleteOrders: false,
    staffCanManagePrintCenter: false,
    staffCanManageUsers: false,
    staffCanViewPosStatistics: false,
    staffCanManageCashSessions: false,
};

const PermissionsContext = createContext<PermissionsContextType>({
    permissions: defaultPermissions,
    loading: true,
    isStaff: false,
    isTenantAdmin: false,
    canAccess: () => false,
    refreshPermissions: async () => { },
});

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [permissions, setPermissions] = useState<StaffPermissions>(defaultPermissions);
    const [loading, setLoading] = useState(true);
    const [isStaff, setIsStaff] = useState(false);
    const [isTenantAdmin, setIsTenantAdmin] = useState(false);

    const fetchPermissions = async () => {
        if (!isAuthenticated) {
            setPermissions(defaultPermissions);
            setIsStaff(false);
            setIsTenantAdmin(false);
            setLoading(false);
            return;
        }

        try {
            logger.debug('Permissions: loading');
            const tenantId = localStorage.getItem('tenantId');
            logger.debug('Permissions: tenantId present?', Boolean(tenantId));

            // Prima ottieni l'utente corrente per determinare i ruoli
            const userResponse = await apiFetch('/api/me');

            if (userResponse.ok) {
                const userData = await userResponse.json();
                const roles = Array.isArray(userData.roles) ? userData.roles : [];
                logger.debug('Permissions: user roles loaded');
                setIsStaff(roles.includes('ROLE_USER'));
                setIsTenantAdmin(roles.includes('ROLE_TENANT_ADMIN') || roles.includes('ROLE_ADMIN'));
            }

            // Poi ottieni i permessi
            const response = await apiFetch('/api/tenant-settings');

            if (response.ok) {
                const data = await response.json();
                logger.debug('Permissions: tenant settings loaded');
                setPermissions(data);
            } else {
                setPermissions(defaultPermissions);
            }
        } catch (error) {
            logger.error('Permissions: failed to load', error);
            setPermissions(defaultPermissions);
            setIsStaff(false);
            setIsTenantAdmin(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPermissions();
    }, [isAuthenticated]);

    const canAccess = (permission: keyof StaffPermissions): boolean => {
        // Tenant Admin e Super Admin hanno sempre accesso a tutto
        if (isTenantAdmin) return true;

        // Staff verifica i permessi
        if (isStaff) {
            return permissions[permission] === true;
        }

        // Altri ruoli non hanno accesso
        return false;
    };

    const refreshPermissions = async () => {
        setLoading(true);
        await fetchPermissions();
    };

    return (
        <PermissionsContext.Provider value={{
            permissions,
            loading,
            isStaff,
            isTenantAdmin,
            canAccess,
            refreshPermissions
        }}>
            {children}
        </PermissionsContext.Provider>
    );
};
